import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { getTestId } from 'helpers/test-helpers';

interface TabProps {
  id: string;
  label: string;
  value: string;
  icon?: React.ReactNode;
  count?: number;
  isActive?: boolean;
  isDisabled?: boolean;
  panelId?: string;
  onClick?: () => void;
  'aria-label'?: string;
}

const Tab = forwardRef<HTMLButtonElement, TabProps>(
  (
    {
      id,
      label,
      value,
      icon,
      count,
      isActive,
      isDisabled,
      panelId,
      onClick,
      'aria-label': ariaLabel,
    },
    ref,
  ): React.JSX.Element => {
    const baseClasses =
      'inline-block pb-1.5 border-b-2 font-semibold leading-6';
    const activeClasses = 'text-navy-800 border-lime-400';
    const inactiveClasses =
      'text-navy-500 border-transparent hover:text-gray-600';
    const disabledClasses = 'text-gray-600 cursor-not-allowed';

    const className = classNames(baseClasses, {
      [disabledClasses]: isDisabled,
      [activeClasses]: isActive,
      [inactiveClasses]: !isActive,
    });

    const dataTestId = getTestId({
      prefix: 'navigation',
      label: value,
      suffix: 'tab',
    });

    return (
      <li role="presentation">
        <button
          ref={ref}
          type="button"
          id={id}
          role="tab"
          aria-selected={isActive}
          aria-controls={panelId}
          tabIndex={isActive ? 0 : -1}
          onClick={onClick}
          className={className}
          disabled={isDisabled}
          aria-label={ariaLabel}
          data-testid={dataTestId}
        >
          {icon || count !== undefined ? (
            <span className="inline-flex items-center gap-1.5">
              {icon}
              {label}
              {count !== undefined && ' '}
              {count !== undefined && (
                <span
                  className="inline-flex size-5 items-center justify-center rounded-full bg-navy-100 text-xs font-semibold text-navy-500"
                  data-testid={`${dataTestId}-count`}
                >
                  {count}
                </span>
              )}
            </span>
          ) : (
            label
          )}
        </button>
      </li>
    );
  },
);

Tab.displayName = 'Tab';

export default Tab;
